import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LogLevel } from './log-level';
import { WebServiceEndpoints } from '@common/web-service-endpoints';
import { ConfigService } from '../config/config.service';
import { SessionDataService } from '../session/session-data.service';
import { sensitiveFieldNames } from '@common/helpers/json-helper';
import { EventCode } from './log-event-code';

export interface ErrorLike {
  name: string;
  message: string;
  code?: string;
}

export interface LogAdditionalData {
  taskReservationId?: string;
  taskId?: string;
  twillioChannelId?: string;
  globalChannelId?: string;
}

@Injectable()
export class LoggingService {
  //only for ngrx
  public static logger: LoggingService = null;

  private readonly _logLevelToNumberMap = new Map<LogLevel, number>([
    [LogLevel.Trace, 0],
    [LogLevel.Debug, 1],
    [LogLevel.Info, 2],
    [LogLevel.Warning, 3],
    [LogLevel.Error, 4],
    [LogLevel.Critical, 5],
    [LogLevel.None, 6],
  ]);

  private _defaultMessageSource = 'Logging service';
  // Pre-compiled regex for sensitive data detection
  private readonly _sensitiveDataRegex: RegExp;

  constructor(
    private readonly http: HttpClient,
    private readonly configService: ConfigService,
    private readonly sessionDataService: SessionDataService,
  ) {
    // Create a single regex with alternation (field1|field2|...)
    const sensitiveFieldsPattern = sensitiveFieldNames
      .map((fieldName) => fieldName.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')) // Escape special regex chars
      .join('|');

    // Combined regex: matches "field": "value", "field": value, field: "value", field: value
    this._sensitiveDataRegex = new RegExp(`"?(${sensitiveFieldsPattern})"?\\s*:\\s*("[^"]*"|[^,}\\n"]+)`, 'gi');
  }

  public async trace(message: string, source: string = this._defaultMessageSource): Promise<void> {
    const logMessage = LoggingService.formatMessage(source, message);
    if (this.sessionDataService.get(SessionDataService.LogToConsole, true)) {
      // eslint-disable-next-line no-console
      console.log(logMessage);
    }
    await this._write(LogLevel.Trace, logMessage);
  }

  public async info(
    message: string,
    source: string = this._defaultMessageSource,
    eventCode: EventCode = EventCode.None,
    additionalData: LogAdditionalData = {},
  ): Promise<void> {
    const logMessage = LoggingService.formatMessage(source, message);
    if (this.sessionDataService.get(SessionDataService.LogToConsole, true)) {
      // eslint-disable-next-line no-console
      console.log(logMessage);
    }
    await this._write(LogLevel.Info, logMessage, eventCode, additionalData);
  }

  public async error(
    error: ErrorLike,
    message?: string,
    from?: string,
    source: string = this._defaultMessageSource,
  ): Promise<void> {
    const errorMessage = LoggingService._convertToLogMessage(error, message, from);
    const logMessage = LoggingService.formatMessage(source, errorMessage);
    if (this.sessionDataService.get(SessionDataService.LogToConsole, true)) {
      console.error(logMessage);
    }
    await this._write(LogLevel.Error, logMessage);
  }

  public async warning(
    error: ErrorLike,
    message?: string,
    from?: string,
    source: string = this._defaultMessageSource,
  ): Promise<void> {
    const errorMessage = LoggingService._convertToLogMessage(error, message, from);
    const logMessage = LoggingService.formatMessage(source, errorMessage);
    if (this.sessionDataService.get(SessionDataService.LogToConsole, true)) {
      console.warn(logMessage);
    }
    await this._write(LogLevel.Warning, logMessage);
  }

  private static _convertToLogMessage(error: ErrorLike, message?: string, from?: string): string {
    let resultMessage = `${LoggingService._serialiseError(error)}`;

    if (message) {
      resultMessage = `${message}\n${resultMessage}`;
    }

    if (from) {
      resultMessage = `${from}\n${resultMessage}`;
    }

    return resultMessage;
  }

  private static _serialiseError(error: ErrorLike): string {
    return JSON.stringify({ name: error.name, message: error.message, code: error.code });
  }

  private async _write(
    logLevel: LogLevel,
    message: string,
    eventCode: EventCode = EventCode.None,
    additionalData: LogAdditionalData = {},
  ): Promise<void> {
    try {
      if (
        this.configService.logSettings &&
        this._logLevelToNumberMap.get(this.configService.logSettings.LogLevel) > this._logLevelToNumberMap.get(logLevel)
      ) {
        return;
      }

      // Sanitize the message to remove sensitive data before sending to the server
      const sanitizedMessage = this.sanitizeLogMessage(message);

      await this.http
        .post(WebServiceEndpoints.Log.write, {
          Level: logLevel,
          Message: sanitizedMessage,
          TaskReservationId: additionalData?.taskReservationId,
          TaskId: additionalData?.taskId,
          TwillioChannelId: additionalData?.twillioChannelId,
          GlobalChannelId: additionalData?.globalChannelId,
          EventCode: eventCode,
        })
        .toPromise();
    } catch (e) {
      console.error(`Log error: ${e}`);
    }
  }

  private sanitizeLogMessage(message: string): string {
    return message.replace(this._sensitiveDataRegex, (match, fieldName) => {
      return `${fieldName}: [SENSITIVE DATA]`;
    });
  }

  private static formatMessage(source: string, message: string): string {
    return `[${source}]: ${message}`;
  }
}
